import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
  InputAdornment,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { Add as AddIcon, FilterList as FilterListIcon, MoreVert, Edit as EditIcon, Delete as DeleteIcon, Search as SearchIcon, ContentCopy as CopyIcon } from '@mui/icons-material';
import { ThemeContext } from '../../components/ThemeContext/ThemeContext';
import AdvancedFilterModal from '../../components/AdvancedFilterModal';
import http from '../../services/http';
import { fetchFilters } from '../../redux/filter/filter.actions';
import { toast } from 'react-toastify';
import { ROTULO_EQUIPE, podeEditarFiltro, rotulosDeCompartilhamento } from '../../utils/compartilhamentoDoFiltro';

const FiltersPage = (props) => {
  const { theme } = useContext(ThemeContext);
  const [filters, setFilters] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [filterToEdit, setFilterToEdit] = useState(null);
  const [modalMode, setModalMode] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuFilter, setMenuFilter] = useState(null);
  const [deleteConfirmAnchor, setDeleteConfirmAnchor] = useState(null);
  const saveHandlerRef = useRef(null);
  const saveAndApplyHandlerRef = useRef(null);
  const [canSave, setCanSave] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const user = typeof window !== 'undefined' ? JSON.parse(localStorage.getItem('user') || '{}') : {};
  const currentUserId = user?.id;

  const loadFilters = useCallback(() => {
    setLoading(true);
    http.get('/filters')
      .then((res) => setFilters(Array.isArray(res.data) ? res.data : []))
      .catch(() => setFilters([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadFilters();
    props.fetchFilters();
  }, [loadFilters, props.fetchFilters]);

  const filteredList = filters.filter((f) => {
    const matchSearch = !searchText.trim() || (f.name || '').toLowerCase().includes(searchText.trim().toLowerCase());
    return matchSearch;
  });

  // Editar: o dono, ou quem tem a permissão dos filtros da equipe (o mesmo
  // portão da api, em `utils/compartilhamentoDoFiltro`). Apagar continua só do
  // dono: consertar é reversível, apagar o filtro que a equipe usa não é.
  const canEdit = (filter) => podeEditarFiltro(filter, currentUserId, props.permissions);
  const canDelete = (filter) => filter.user_id === currentUserId;

  const handleOpenCreate = () => {
    setFilterToEdit(null);
    setModalMode('create');
    setModalOpen(true);
  };

  const handleOpenEdit = (filter) => {
    setFilterToEdit(filter);
    setModalMode('edit');
    setModalOpen(true);
    setMenuAnchor(null);
    setMenuFilter(null);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setFilterToEdit(null);
    setModalMode(null);
    loadFilters();
    props.fetchFilters();
  };

  const handleSaveClick = async () => {
    if (!saveHandlerRef.current) {
      toast.error('Não foi possível salvar. Tente fechar e abrir o formulário novamente.');
      return;
    }
    try {
      // `false` = o formulário recusou salvar (nome vazio, coluna sem item,
      // condição que não casa nada). Fechar assim mesmo jogaria fora o que a
      // pessoa digitou, e ela só veria um aviso sumindo na tela.
      const salvou = await saveHandlerRef.current();
      if (salvou !== false) handleCloseModal();
    } catch {
      // Erro exibido em AdvancedFilterModal
    }
  };

  const handleDeleteFilter = (filter) => {
    setDeleteConfirmAnchor(null);
    setMenuFilter(null);
    if (!canDelete(filter)) return;
    http.delete(`/filters/${filter.id}`)
      .then(() => {
        toast.success('Filtro excluído.');
        loadFilters();
        props.fetchFilters();
      })
      .catch((err) => toast.error(err.response?.data?.message || 'Erro ao excluir filtro.'));
  };

  // Duplica o filtro/kanban (item 23): cria "Cópia de ..." para ajustar só o necessário.
  const handleCloneFilter = (filter) => {
    setMenuAnchor(null);
    setMenuFilter(null);
    http.post(`/filters/${filter.id}/clone`)
      .then(() => {
        toast.success('Filtro duplicado.');
        loadFilters();
        props.fetchFilters();
      })
      .catch((err) => toast.error(err.response?.data?.message || 'Não foi possível duplicar o filtro.'));
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, margin: '0 auto' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
        <Typography variant="h5" fontWeight={700} sx={{ color: theme.palette.text.primary }}>
          Filtros
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Crie e gerencie filtros para organizar a visualização dos tickets. Você vê os filtros padrão (visíveis a todos) e os seus. Apenas filtros que você criou podem ser editados ou excluídos por você.
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 3 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenCreate}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          Novo filtro
        </Button>
        <TextField
          placeholder="Buscar por nome..."
          size="small"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 220 }}
        />
      </Box>

      {loading ? (
        <Typography color="text.secondary">Carregando...</Typography>
      ) : filteredList.length === 0 ? (
        <Card variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <FilterListIcon sx={{ fontSize: 48, color: theme.palette.text.secondary, mb: 1 }} />
          <Typography color="text.secondary" gutterBottom>
            {searchText.trim() ? 'Nenhum filtro encontrado com esse nome.' : 'Nenhum filtro disponível. Crie um filtro ou aguarde filtros padrão da equipe.'}
          </Typography>
          {!searchText.trim() && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate} sx={{ mt: 2, textTransform: 'none' }}>
              Criar primeiro filtro
            </Button>
          )}
        </Card>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {filteredList.map((filter) => (
            <Card
              key={filter.id}
              variant="outlined"
              sx={{
                cursor: canEdit(filter) ? 'pointer' : 'default',
                '&:hover': canEdit(filter) ? { borderColor: theme.palette.primary.main, bgcolor: theme.palette.action.hover } : {},
              }}
              onClick={() => canEdit(filter) && handleOpenEdit(filter)}
            >
              <CardContent sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle1" fontWeight={600} sx={{ color: theme.palette.text.primary }}>
                    {filter.name}
                  </Typography>
                  {filter.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {filter.description}
                    </Typography>
                  )}
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                    {filter.settings?.view_type === 'kanban' ? (
                      <Chip
                        label="Kanban"
                        size="small"
                        variant="outlined"
                        sx={{
                          color: theme.palette.text.secondary,
                          borderColor: theme.palette.text.secondary,
                          '& .MuiChip-label': { color: theme.palette.text.secondary },
                        }}
                      />
                    ) : (
                      <Chip
                        label="Listagem"
                        size="small"
                        variant="outlined"
                        sx={{
                          color: theme.palette.text.secondary,
                          borderColor: theme.palette.text.secondary,
                          '& .MuiChip-label': { color: theme.palette.text.secondary },
                        }}
                      />
                    )}
                    {/* Um rótulo só para as duas formas de compartilhar; a
                      * antiga se identifica como antiga. Ver
                      * utils/compartilhamentoDoFiltro. */}
                    {rotulosDeCompartilhamento(filter).map((rotulo) => (
                      <Chip
                        key={rotulo}
                        label={rotulo}
                        size="small"
                        color={rotulo === ROTULO_EQUIPE ? 'primary' : 'default'}
                        variant="outlined"
                      />
                    ))}
                    {filter.settings?.visible_in_panel === false && (
                      <Chip
                        label="Oculto no painel"
                        size="small"
                        variant="outlined"
                        sx={{
                          color: theme.palette.text.secondary,
                          borderColor: theme.palette.text.secondary,
                          '& .MuiChip-label': { color: theme.palette.text.secondary },
                        }}
                      />
                    )}
                  </Box>
                </Box>
                {canEdit(filter) && (
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setMenuFilter(filter);
                      setMenuAnchor(e.currentTarget);
                    }}
                  >
                    <MoreVert fontSize="small" />
                  </IconButton>
                )}
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => { setMenuAnchor(null); setMenuFilter(null); }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          onClick={() => menuFilter && handleOpenEdit(menuFilter)}
        >
          <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Editar" />
        </MenuItem>
        <MenuItem onClick={() => menuFilter && handleCloneFilter(menuFilter)}>
          <ListItemIcon><CopyIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Duplicar" />
        </MenuItem>
        {menuFilter && canDelete(menuFilter) && (
          <MenuItem
            onClick={() => {
              setDeleteConfirmAnchor(menuAnchor);
              setMenuAnchor(null);
            }}
            sx={{ color: theme.palette.error.main }}
          >
            <ListItemIcon><DeleteIcon fontSize="small" sx={{ color: theme.palette.error.main }} /></ListItemIcon>
            <ListItemText primary="Excluir" />
          </MenuItem>
        )}
      </Menu>

      <Menu
        anchorEl={deleteConfirmAnchor}
        open={Boolean(deleteConfirmAnchor)}
        onClose={() => setDeleteConfirmAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <MenuItem disabled><ListItemText primary="Excluir este filtro?" secondary="Esta ação não pode ser desfeita." /></MenuItem>
        <MenuItem
          onClick={() => menuFilter && handleDeleteFilter(menuFilter)}
          sx={{ color: theme.palette.error.main }}
        >
          Sim, excluir
        </MenuItem>
        <MenuItem onClick={() => setDeleteConfirmAnchor(null)}>Cancelar</MenuItem>
      </Menu>

      <Dialog
        open={modalOpen}
        onClose={handleCloseModal}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            width: 'min(1280px, 96vw)',
            maxWidth: '96vw',
            height: 'min(900px, 92vh)',
            maxHeight: '92vh',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <DialogTitle sx={{ flexShrink: 0 }}>{modalMode === 'create' ? 'Novo filtro' : (filterToEdit?.name || 'Editar filtro')}</DialogTitle>
        <DialogContent dividers sx={{ p: 0, flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <AdvancedFilterModal
            open={true}
            isSlidePanel
            handleClose={handleCloseModal}
            fields={props.fields || []}
            onFilterApplied={handleCloseModal}
            initialFilter={filterToEdit}
            initialMode={modalMode}
            onSaveReady={(data) => {
              saveHandlerRef.current = data.handleSave;
              saveAndApplyHandlerRef.current = data.handleSaveAndApply;
              setCanSave(data.canSave);
              setIsCreating(data.isCreating || false);
              setHasChanges(data.hasChanges || false);
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5 }}>
          <Button onClick={handleCloseModal} color="inherit">Cancelar</Button>
          <Button variant="contained" onClick={handleSaveClick} disabled={!canSave}>
            Salvar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const mapStateToProps = (state) => ({
  fields: state.field?.fields || [],
  // Para saber quem pode consertar filtro da equipe — ver podeEditarFiltro.
  permissions: state.user?.permissions || [],
});

const mapDispatchToProps = (dispatch) => ({
  fetchFilters: () => dispatch(fetchFilters()),
});

export default connect(mapStateToProps, mapDispatchToProps)(FiltersPage);
